import { Link } from "react-router-dom";
import { ArrowLeft, ChevronUp, ChevronDown } from "lucide-react";
import { ShortsActions } from "./ShortsActions";

const pillLabel = (isFeedMode, current, channel) => {
  if (!isFeedMode) return channel.name;
  return current?.channel_name ? `Pour toi · ${current.channel_name}` : "Pour toi";
};

const ChannelPill = ({ isFeedMode, current, channel }) => (
  <div className="pointer-events-none absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-md flex items-center gap-2 text-white" data-testid="shorts-channel-pill">
    <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: (isFeedMode && current?.channel_color) || channel.color }} />
    <span className="text-[10px] uppercase tracking-[0.3em]">{pillLabel(isFeedMode, current, channel)}</span>
  </div>
);

const ProgressDots = ({ videos, index }) => (
  <div className="absolute top-1/2 right-3 -translate-y-1/2 flex flex-col gap-1 pointer-events-none" data-testid="shorts-progress">
    {videos.slice(0, 10).map((v, i) => (
      <span key={v.id} className={`w-1 rounded-full transition-all ${i === index ? "h-6 bg-[color:var(--accent)]" : "h-1 bg-white/30"}`} />
    ))}
    {videos.length > 10 && <span className="text-[8px] text-white/50 mt-1 text-center font-mono">{index + 1}/{videos.length}</span>}
  </div>
);

const NavArrow = ({ onClick, testid, label, pos, children }) => (
  <button onClick={onClick} data-testid={testid} aria-label={label} className={`hidden md:flex absolute left-full ml-4 ${pos} -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center transition`}>
    {children}
  </button>
);

/** Everything drawn on top of the Shorts stage: pill, dots, info, actions, arrows. */
export const ShortsOverlay = ({ feed, isFeedMode }) => {
  const { channel, videos, index, current, muted, toggleMuted, next, prev } = feed;
  return (
    <>
      <div className="pointer-events-none absolute top-0 left-0 right-0 h-28 bg-gradient-to-b from-black/80 to-transparent" />
      <Link to="/channels" className="pointer-events-auto absolute top-4 left-4 inline-flex items-center justify-center w-10 h-10 rounded-full bg-black/50 backdrop-blur-md text-white hover:bg-black/80 transition" data-testid="shorts-back" aria-label="Retour aux chaînes">
        <ArrowLeft className="w-4 h-4" />
      </Link>
      <ChannelPill isFeedMode={isFeedMode} current={current} channel={channel} />
      <ProgressDots videos={videos} index={index} />

      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-4 pb-6 pointer-events-none">
        <div className="flex items-end gap-3">
          <div className="flex-1 min-w-0 pointer-events-auto">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent)] mb-1">
              {isFeedMode ? (current?.channel_category || "Pour toi") : channel.category}
            </div>
            <div className="font-display text-lg leading-tight text-white line-clamp-3" data-testid="shorts-title">{current?.title}</div>
            {videos.length > 1 && <div className="mt-3 text-[10px] text-white/60">Swipe / molette / ↑ ↓ pour naviguer</div>}
          </div>
          <ShortsActions current={current} muted={muted} onToggleMuted={toggleMuted} />
        </div>
      </div>

      {index > 0 && <NavArrow onClick={prev} testid="shorts-prev" label="Short précédent" pos="top-1/3"><ChevronUp className="w-5 h-5" /></NavArrow>}
      {index < videos.length - 1 && <NavArrow onClick={next} testid="shorts-next" label="Short suivant" pos="top-2/3"><ChevronDown className="w-5 h-5" /></NavArrow>}
    </>
  );
};
